import axios from 'axios';

const MISTRAL_API_KEY = process.env.REACT_APP_MISTRAL_API_KEY;

// Available Mistral models
export const MISTRAL_MODELS = {
  'mistral-tiny': 'Mistral Tiny',
  'mistral-small': 'Mistral Small',
  'mistral-medium': 'Mistral Medium',
  'mistral-large': 'Mistral Large'
};

// Generation configuration
const generationConfig = {
  temperature: 0.4,
  top_p: 0.95,
  max_tokens: 2048,
};

// Helper function to get system instruction
function getSystemInstruction(standards_title = '', question = '', section_title = '', official_regulation = '', related_ar = '') {
  return `You are a CSRD consultant with deep expertise in ESRS, particularly in "${section_title}", responsible for drafting the company's official CSRD report. You must write as if you were part of the company, ensuring the response is fully aligned with regulatory expectations, internal policies, and best practices.

Please analyze the document the user will upload and answer this question: "${question}" as part of the company's official response under "${section_title}" related to the "${standards_title}" (optional "${official_regulation}" and application requirements "${related_ar}"). 

Your response must be written as if you were an internal representative of the company, providing definitive statements rather than recommendations. Avoid external phrasing such as "the company should..." and instead write in a direct, authoritative, and company-integrated manner. 

IMPORTANT: Keep your response concise and focused, approximately one page in length (around 500 words). Prioritize the most relevant and impactful information while maintaining completeness and accuracy. Structure your response with clear paragraphs and use bullet points when appropriate to enhance readability.

If relevant information is found in the document, cite name, numbers, job titles, etc in your response in order to be as precise as possible. Ensure compliance with CSRD and ESRS requirements while maintaining language consistency (French if the document is in French, English if in English). 

If you cannot access or read the document content, please indicate the specific error encountered.`;
}

// Function to convert Uint8Array to base64
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Function to split document content into chunks based on size
function splitDocumentIntoChunks(docContent, maxChunkSize = 15000) { // Using smaller chunks for Mistral
  if (docContent instanceof Uint8Array) {
    const chunks = [];
    for (let i = 0; i < docContent.length; i += maxChunkSize) {
      chunks.push(docContent.slice(i, i + maxChunkSize));
    }
    return chunks;
  } else if (typeof docContent === 'string') {
    const chunks = [];
    for (let i = 0; i < docContent.length; i += maxChunkSize) {
      chunks.push(docContent.slice(i, i + maxChunkSize));
    }
    return chunks;
  }
  throw new Error('Invalid document content type. Must be Uint8Array or string.');
}

// Function to make API call to Mistral
async function callMistralAPI(messages, modelName, config) {
  if (!MISTRAL_API_KEY) {
    throw new Error('Mistral API is not configured yet. Please try another model.');
  }

  try {
    const response = await axios.post(
      'https://api.mistral.ai/v1/chat/completions',
      {
        model: modelName,
        messages,
        ...config
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MISTRAL_API_KEY}`
        }
      }
    );

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Error calling Mistral API:', error);
    throw error;
  }
}

// Main function to analyze documents
export const analyzeDocuments = async (
  documentsContent,
  question,
  standards_title = '',
  section_title = '',
  paragraph = '',
  related_ar = '',
  modelName = 'mistral-medium'
) => {
  if (!MISTRAL_API_KEY) {
    throw new Error('Mistral API is not configured yet. Please try another model.');
  }

  const maxRetries = 5;
  let retryDelay = 1000;
  const systemInstruction = getSystemInstruction(
    standards_title,
    question,
    section_title,
    paragraph,
    related_ar
  );
  let combinedResponse = '';

  try {
    // Process each document's chunks sequentially
    for (const docContent of documentsContent) {
      const chunks = splitDocumentIntoChunks(docContent);
      
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const chunk = chunks[chunkIndex];
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            // Convert chunk to base64 if needed
            const encodedChunk = chunk instanceof Uint8Array 
              ? arrayBufferToBase64(chunk)
              : btoa(unescape(encodeURIComponent(chunk)));

            // Prepare messages for the API call
            const messages = [
              { role: 'system', content: systemInstruction },
              { role: 'user', content: `This is chunk ${chunkIndex + 1} of ${chunks.length} from document ${documentsContent.indexOf(docContent) + 1} of ${documentsContent.length}. Here is the content in base64: ${encodedChunk}` }
            ];

            const responseText = await callMistralAPI(messages, modelName, generationConfig);

            if (!responseText) {
              throw new Error('Empty response from AI model');
            }

            // Append the chunk's analysis to the combined response
            if (combinedResponse) {
              combinedResponse += '\n\n---\n\n';
            }
            combinedResponse += responseText.trim();
            break; // Success, move to next chunk
            
          } catch (error) {
            if (error.response?.status === 429) {
              if (attempt === maxRetries) {
                throw new Error('Rate limit exceeded. Max retries reached.');
              }
              const currentDelay = retryDelay;
              console.warn(`Rate limit exceeded. Retrying in ${currentDelay / 1000} seconds...`);
              await new Promise(resolve => setTimeout(resolve, currentDelay));
              retryDelay *= 2;
            } else {
              console.error('Error processing chunk:', error);
              throw error;
            }
          }
        }
      }
    }

    return combinedResponse || 'No relevant information found in the analyzed documents.';
    
  } catch (error) {
    console.error('Error analyzing documents:', error);
    throw error;
  }
};

// Keep the original analyzeDocument for backward compatibility
export const analyzeDocument = async (
  documentContent,
  question,
  standards_title = '',
  section_title = '',
  paragraph = '',
  related_ar = '',
  modelName = 'mistral-medium'
) => {
  return analyzeDocuments(
    [documentContent],
    question,
    standards_title,
    section_title,
    paragraph,
    related_ar,
    modelName
  );
}; 